import type * as React from 'react';
import { StyleSheet, View } from 'react-native';

import { useScreenType } from '@gitbook/spine-react';

import { AnimateLifecycle } from '../Animate';
import { zIndexes } from '../constants';
import type { PortalRelativeWrapperProps } from '../Portal';

/*
 * Component to pass to Portal to position a popover.
 * On mobile it shows the backdrop.
 */
export function PopoverPortalWrapper(props: PortalRelativeWrapperProps): React.ReactElement {
    const { opened, children, style } = props;
    const { isMobile } = useScreenType();

    if (isMobile) {
        return <>{opened ? children : null}</>;
    }

    return (
        <AnimateLifecycle>
            {opened ? (
                <View pointerEvents="none" style={[baseStyles.popoverPortalWrapper, style]}>
                    {children}
                </View>
            ) : null}
        </AnimateLifecycle>
    );
}

/*
 * Component to pass to Portal to position a popover without unmounting the component. It uses visibility and
 * z-index to hide the content of the popover rather than unmounting. Unmounting can cause issues with the animations
 * as refs children stay alive (but mutated to null) when given to a parent that will not be unmounted. Use if you
 * run into any ref mutation issues.
 * On mobile it shows the backdrop.
 */
export function VisibilityPopoverPortalWrapper(
    props: PortalRelativeWrapperProps
): React.ReactElement {
    const { opened, children, style } = props;
    const { isMobile } = useScreenType();

    if (isMobile) {
        return <>{opened ? children : null}</>;
    }

    return (
        <AnimateLifecycle>
            <View
                pointerEvents="none"
                style={[
                    baseStyles.popoverPortalWrapper,
                    style,
                    !opened ? baseStyles.popoverInvisible : null,
                ]}
            >
                {children}
            </View>
        </AnimateLifecycle>
    );
}

const baseStyles = StyleSheet.create({
    popoverPortalWrapper: {
        zIndex: zIndexes.POPOVER,
        position: 'relative',
        // @ts-ignore
        display: 'inline-block',
    },
    popoverInvisible: {
        zIndex: -1,
        display: 'none',
        // @ts-ignore
        pointerEvents: 'none',
    },
});
